/**
 * IndexedDB-backed offline write queue — Impo.
 *
 * Persists axios write requests that failed due to a network error so that
 * they can be replayed automatically once connectivity is restored.
 *
 * Contract:
 *   - Only JSON writes are queueable (POST/PATCH/PUT/DELETE with a JSON body).
 *     Multipart file uploads are intentionally skipped — we don't want to
 *     serialize large binary blobs into IndexedDB. The user is told to retry
 *     the upload when they're back online.
 *   - When a request is enqueued, the axios interceptor resolves the caller
 *     with a synthetic response so the UI can still show optimistic feedback.
 *     A queue id + `__offline: true` marker are attached to the returned data.
 *   - The queue is flushed automatically:
 *       * whenever navigator regains connectivity ('online' event)
 *       * every 30 seconds while online (background sweeper)
 *       * on explicit user tap of the OfflineBanner 'Sync now' button.
 *
 * Public API:
 *   enqueue(item)   → stores an item and returns its queue id
 *   list()          → all queued items (chronological)
 *   remove(id)      → remove an item after successful replay
 *   count()         → number of pending items
 *   flush(api)      → attempts to replay everything (returns { synced, failed })
 *   subscribe(fn)   → subscribe to queue-count change events, returns unsubscribe
 */

const DB_NAME = "impo";
const DB_VERSION = 1;
const STORE_NAME = "write_queue";

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      return reject(new Error("IndexedDB not available"));
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

// -------- Subscribers (in-memory pubsub for the badge count) --------
const subs = new Set();
function notify() {
  count().then((n) => subs.forEach((fn) => { try { fn(n); } catch {} })).catch(() => {});
}
export function subscribe(fn) { subs.add(fn); return () => subs.delete(fn); }

// -------- API --------
function randomId() {
  return (
    (Date.now().toString(36)) + "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

export async function enqueue({ method, url, body, headers }) {
  const item = {
    id: randomId(),
    method: (method || "POST").toUpperCase(),
    url,
    body: body ?? null,      // MUST be JSON-serializable
    headers: headers || {},
    created_at: new Date().toISOString(),
    retry_count: 0,
    last_error: null,
  };
  await withStore("readwrite", (store) => store.put(item));
  notify();
  return item.id;
}

export async function list() {
  return withStore("readonly", (store) => new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = req.result || [];
      rows.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  }));
}

export async function remove(id) {
  await withStore("readwrite", (store) => store.delete(id));
  notify();
}

export async function count() {
  return withStore("readonly", (store) => new Promise((resolve, reject) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

export async function clearAll() {
  await withStore("readwrite", (store) => store.clear());
  notify();
}

export function isNetworkError(err) {
  if (!err) return false;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  if (err.message === "Network Error") return true;
  if (typeof window !== "undefined" && navigator && navigator.onLine === false) return true;
  return false;
}

/**
 * Replay all queued items sequentially.
 *
 * @param {import("axios").AxiosInstance} api  Axios instance (used for auth token + baseURL)
 * @returns {Promise<{synced:number, failed:number, remaining:number}>}
 */
export async function flush(api) {
  const items = await list();
  let synced = 0;
  let failed = 0;
  for (const it of items) {
    if (typeof window !== "undefined" && navigator && navigator.onLine === false) {
      break; // don't waste cycles offline
    }
    try {
      await api.request({
        method: it.method,
        url: it.url,
        data: it.body,
        headers: { ...(it.headers || {}) },
      });
      await remove(it.id);
      synced += 1;
    } catch (err) {
      const status = err?.response?.status;
      // 4xx (except 401/408/429) are client-side and won't succeed by replay.
      // Drop them from the queue so we don't loop forever, and keep count clean.
      if (status && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429) {
        // eslint-disable-next-line no-console
        console.warn("[offline-queue] dropping unrecoverable item", it.method, it.url, status);
        await remove(it.id);
        failed += 1;
      } else if (isNetworkError(err)) {
        // Still offline, stop trying now
        break;
      } else {
        // Transient server issue — keep in queue, bump retry_count
        await withStore("readwrite", (store) => {
          const req = store.get(it.id);
          req.onsuccess = () => {
            const cur = req.result;
            if (cur) {
              cur.retry_count = (cur.retry_count || 0) + 1;
              cur.last_error = err?.message || "error";
              store.put(cur);
            }
          };
        });
        failed += 1;
      }
    }
  }
  const remaining = await count();
  return { synced, failed, remaining };
}
